<!--
  Modal: pick existing elements by inventory type to link into a LIST table via `postLink`.
  `parentObjectListId`, `listGroupId`, `tableFieldId` feed the link API; bound child ids only affect row filtering.
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import { useHttp, useTranslate } from '@/shared/lib'
import { VIcons } from '@/shared/ui'
import { usePendingTableBindingsOptional } from '../model/usePendingTableBindings'
import { filterElementsRowsForLinkModal } from '../model/linkTableModalElements'

const props = defineProps<{
  modelValue: boolean
  /** Map group id for `/inventory/objects/{id}/...` (route `object_id`). */
  inventoryGroupObjectId: string
  /** Parent row `object_list_id` (for Task 4 `postLink`). */
  parentObjectListId: number
  /** LIST group id from layout (for Task 4 `postLink`). */
  listGroupId: number
  /** Table field id from layout (for Task 4 `postLink`). */
  tableFieldId: string
  /** Subtype id for elements-by-type listing; must be positive when the modal is used. */
  inventTypeId: number
  /**
   * Already-linked child `object_list_id` values; those rows are hidden.
   * Passed as `number[]` for ergonomics; converted to a `Set` internally for filtering.
   */
  boundChildIds: number[]
}>()

/**
 * Emits `attached` after a successful link + query invalidation.
 */
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  attached: []
}>()

const { trs } = useTranslate()
const http = useHttp()
const pendingBindings = usePendingTableBindingsOptional()

const filter = ref({
  search: '',
  page: 1,
  per_page: 30,
})

const searchInput = ref('')

const selected = ref<number[]>([])

const boundIdsSet = computed(() => new Set(props.boundChildIds.map((id) => Number(id)).filter((id) => id > 0)))

const queryEnabled = computed(() => props.modelValue && props.inventTypeId > 0)

const elementsQuery = useQuery({
  queryKey: computed(() => [
    'passport_runtime_link_table_modal_elements',
    props.inventoryGroupObjectId,
    props.inventTypeId,
    filter.value.search,
    filter.value.page,
    filter.value.per_page,
  ]),
  queryFn: async () => {
    const searchParams = new URLSearchParams(filter.value as Record<string, string>)
    const url = `/inventory/objects/${props.inventoryGroupObjectId}/inventory-types/${props.inventTypeId}/elements?${searchParams}`
    return http.get(url)
  },
  enabled: queryEnabled,
  placeholderData: keepPreviousData,
  retry: false,
})

/** Axios response after interceptors: `data` = rows, `payload.total` = count. */
const rawRows = computed(() => {
  const res = elementsQuery.data.value as { data?: unknown; payload?: { total?: number } } | undefined
  if (!res) return [] as { object_list_id?: number; name?: string }[]
  const d = res.data
  if (Array.isArray(d)) return d as { object_list_id?: number; name?: string }[]
  return []
})

const total = computed(() => Number((elementsQuery.data.value as { payload?: { total?: number } })?.payload?.total ?? 0))

const tableItems = computed(() => filterElementsRowsForLinkModal(rawRows.value, boundIdsSet.value))

const headers = computed(() => [
  { title: trs('itf.id'), key: 'object_list_id', sortable: false },
  { title: trs('itf.name_label'), key: 'name', sortable: false },
])

const isLoading = computed(() => elementsQuery.isFetching.value)

function close() {
  emit('update:modelValue', false)
}

function applySearch() {
  filter.value.search = searchInput.value
  filter.value.page = 1
}

function resetOnOpen() {
  filter.value = { search: '', page: 1, per_page: 30 }
  searchInput.value = ''
  selected.value = []
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) resetOnOpen()
  },
)

function onAttach() {
  const ids = [...selected.value]
  if (!ids.length) return
  for (const child_object_list_id of ids) {
    const row = rawRows.value.find((r) => Number(r.object_list_id) === child_object_list_id)
    pendingBindings.stageLink({
      list_group_id: props.listGroupId,
      table_field_id: props.tableFieldId,
      child_object_list_id,
      name: row?.name,
    })
  }
  emit('attached')
  emit('update:modelValue', false)
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    attach="body"
    :z-index="3400"
    max-width="1400"
    scrollable
    content-class="pb-shell-dialog"
    :retain-focus="false"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="prt-link-table-modal__card" rounded="xl">
      <VToolbar
        density="compact"
        color="surface"
        class="prt-link-table-modal__toolbar px-4 px-sm-6"
        flat
      >
        <VToolbarTitle class="text-h6 font-weight-bold">
          {{ trs('itf.passport_runtime_link_table_modal_title') }}
        </VToolbarTitle>
        <VSpacer />
        <VBtn icon variant="text" @click="close">
          <VIcons type="close" />
        </VBtn>
      </VToolbar>

      <VCardText class="prt-link-table-modal__body">
        <div class="prt-link-table-modal__body-inner d-flex flex-column gap-3">
          <VTextField
            v-model="searchInput"
            density="compact"
            variant="outlined"
            hide-details="auto"
            clearable
            :label="trs('itf.passport_runtime_link_table_search_label')"
            @keyup.enter="applySearch"
          />

          <VDataTableServer
            v-model="selected"
            v-model:page="filter.page"
            v-model:items-per-page="filter.per_page"
            :loading="isLoading"
            :items="tableItems"
            :headers
            :items-length="total"
            :items-per-page-options="[10, 30, 50]"
            show-select
            item-value="object_list_id"
          />
        </div>
      </VCardText>

      <VCardActions class="prt-link-table-modal__actions px-4 px-sm-6 py-3">
        <VSpacer />
        <VBtn variant="text" @click="close">
          {{ trs('itf.passport_runtime_link_table_cancel') }}
        </VBtn>
        <VBtn
          color="primary"
          :disabled="selected.length === 0"
          @click="onAttach"
        >
          {{ trs('itf.passport_runtime_attach_selected') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
/* Flex column + min-height:0 so the table scrolls inside the body and footer actions stay visible. */
.prt-link-table-modal__card {
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 980px);
  overflow: hidden;
  border-radius: 16px;
}

.prt-link-table-modal__toolbar {
  flex-shrink: 0;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

.prt-link-table-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 16px !important;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  overflow-x: hidden;
  overflow-y: auto;
}

@media (min-width: 600px) {
  .prt-link-table-modal__body {
    padding: 24px !important;
  }
}

.prt-link-table-modal__body-inner {
  flex: 1 1 auto;
  min-height: 0;
}

.prt-link-table-modal__actions {
  flex-shrink: 0;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgb(var(--v-theme-surface));
  border-radius: 0 0 16px 16px;
  overflow: visible;
}
</style>

<style lang="scss">
/* Match PassportBuilderShellModal.vue */
.pb-shell-dialog {
  padding: 16px;
  box-sizing: border-box;
}
</style>
